import * as t from 'io-ts';

import {ActivitySourceTypesEnum} from './activitySourceModel';
import {makeEnumRuntimeType} from './apiTypes';
import {RecipientModel, RecipientRolesEnum} from './recipientModel';
import {RRuleBundleType} from './ruleBundleModel';
import {parseTag, RTag} from './tagModel';

/*
 * Enums.
 */

export enum AnalyticsTimelineEventsEnum {
  ARCHIVE = 'archive',
  ASSIGN = 'assign',
  CONV_JOIN = 'conv_split',
  CONV_SPLIT = 'conv_join',
  INBOUND = 'inbound',
  MOVE = 'move',
  OUTBOUND = 'outbound',
  OUT_REPLY = 'out_reply',
  REMINDER = 'reminder',
  REOPEN = 'reopen',
  SNOOZE = 'snooze', // This event type is not from backend and is only used to type an archive with a reminder.
  TAG = 'tag',
  UNASSIGN = 'unassign',
  UNTAG = 'untag',
}

/*
 * Runtime types.
 */

export const RAnalyticsTimelineEventType = makeEnumRuntimeType(AnalyticsTimelineEventsEnum);

const RAnalyticsTimelineEventBase = t.type({
  id: t.number,
  date: t.number,
});

const RAnalyticsTimelineEventSource = t.intersection([
  t.type({
    label: t.union([t.undefined, t.string]),
  }),
  t.partial({
    ruleId: t.number,
    ruleBundleId: t.number,
    ruleBundleType: RRuleBundleType,
    ruleNamespace: t.string,
  }),
]);

const RAnalyticsTimelineEventArchived = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.ARCHIVE),
    archivedBy: RAnalyticsTimelineEventSource,
    archivedIn: t.string,
  }),
]);

const RAnalyticsTimelineEventAssigned = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.ASSIGN),
    assignedBy: RAnalyticsTimelineEventSource,
    assignedTo: t.union([t.undefined, t.string]),
  }),
]);

const RAnalyticsTimelineEventUnassigned = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.UNASSIGN),
    unassignedBy: RAnalyticsTimelineEventSource,
    unassignedFrom: t.union([t.undefined, t.string]),
  }),
]);

const RAnalyticsTimelineEventInbound = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.INBOUND),
    messageUid: t.string,
  }),
]);

const RAnalyticsTimelineEventOutbound = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.OUTBOUND),
    messageUid: t.string,
  }),
]);

const RAnalyticsTimelineEventOutReply = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.OUT_REPLY),
    messageUid: t.string,
  }),
]);

const RAnalyticsTimelineEventConvJoined = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.CONV_JOIN),
    convJoinedBy: RAnalyticsTimelineEventSource,
  }),
]);

const RAnalyticsTimelineEventConvSplitted = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.CONV_SPLIT),
    convSplittedBy: RAnalyticsTimelineEventSource,
  }),
]);

const RAnalyticsTimelineEventMoved = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.MOVE),
    movedIn: t.string,
    movedBy: RAnalyticsTimelineEventSource,
  }),
]);

const RAnalyticsTimelineEventReminder = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.REMINDER),
    reminderBy: RAnalyticsTimelineEventSource,
  }),
]);

const RAnalyticsTimelineEventReopened = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.REOPEN),
    reopenedIn: t.string,
    reopenedBy: RAnalyticsTimelineEventSource,
  }),
]);

const RAnalyticsTimelineEventSnoozed = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.SNOOZE),
    snoozedBy: RAnalyticsTimelineEventSource,
    reminderId: t.union([t.undefined, t.number]),
  }),
]);

const RAnalyticsTimelineEventTagged = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.TAG),
    taggedBy: RAnalyticsTimelineEventSource,
    tag: RTag,
  }),
]);

const RAnalyticsTimelineEventUntagged = t.intersection([
  RAnalyticsTimelineEventBase,
  t.type({
    type: t.literal(AnalyticsTimelineEventsEnum.UNTAG),
    untaggedBy: RAnalyticsTimelineEventSource,
    tag: RTag,
  }),
]);

const RORAnalyticsTimelineEventArchived = t.readonly(RAnalyticsTimelineEventArchived);
const RORAnalyticsTimelineEventAssigned = t.readonly(RAnalyticsTimelineEventAssigned);
const RORAnalyticsTimelineEventUnassigned = t.readonly(RAnalyticsTimelineEventUnassigned);
const RORAnalyticsTimelineEventInbound = t.readonly(RAnalyticsTimelineEventInbound);
const RORAnalyticsTimelineEventOutbound = t.readonly(RAnalyticsTimelineEventOutbound);
const RORAnalyticsTimelineEventOutReply = t.readonly(RAnalyticsTimelineEventOutReply);
const RORAnalyticsTimelineEventConvJoined = t.readonly(RAnalyticsTimelineEventConvJoined);
const RORAnalyticsTimelineEventConvSplitted = t.readonly(RAnalyticsTimelineEventConvSplitted);
const RORAnalyticsTimelineEventMoved = t.readonly(RAnalyticsTimelineEventMoved);
const RORAnalyticsTimelineEventReminder = t.readonly(RAnalyticsTimelineEventReminder);
const RORAnalyticsTimelineEventReopened = t.readonly(RAnalyticsTimelineEventReopened);
const RORAnalyticsTimelineEventSnoozed = t.readonly(RAnalyticsTimelineEventSnoozed);
const RORAnalyticsTimelineEventTagged = t.readonly(RAnalyticsTimelineEventTagged);
const RORAnalyticsTimelineEventUntagged = t.readonly(RAnalyticsTimelineEventUntagged);

const RAnalyticsTimelineEventDirty = t.union([
  RAnalyticsTimelineEventArchived,
  RORAnalyticsTimelineEventAssigned,
  RORAnalyticsTimelineEventUnassigned,
  RORAnalyticsTimelineEventInbound,
  RORAnalyticsTimelineEventOutbound,
  RORAnalyticsTimelineEventOutReply,
  RORAnalyticsTimelineEventConvJoined,
  RORAnalyticsTimelineEventConvSplitted,
  RORAnalyticsTimelineEventMoved,
  RORAnalyticsTimelineEventReminder,
  RORAnalyticsTimelineEventReopened,
  RORAnalyticsTimelineEventSnoozed,
  RORAnalyticsTimelineEventTagged,
  RORAnalyticsTimelineEventUntagged,
]);

/*
 * Types.
 */

export interface AnalyticsTimelineEventArchived extends t.TypeOf<typeof RORAnalyticsTimelineEventArchived> {}
export interface AnalyticsTimelineEventAssigned extends t.TypeOf<typeof RORAnalyticsTimelineEventAssigned> {}
export interface AnalyticsTimelineEventUnassigned
  extends t.TypeOf<typeof RORAnalyticsTimelineEventUnassigned> {}
export interface AnalyticsTimelineEventConvJoined
  extends t.TypeOf<typeof RORAnalyticsTimelineEventConvJoined> {}
export interface AnalyticsTimelineEventConvSplitted
  extends t.TypeOf<typeof RORAnalyticsTimelineEventConvSplitted> {}
export interface AnalyticsTimelineEventMoved extends t.TypeOf<typeof RORAnalyticsTimelineEventMoved> {}
export interface AnalyticsTimelineEventInbound extends t.TypeOf<typeof RORAnalyticsTimelineEventInbound> {}
export interface AnalyticsTimelineEventOutbound extends t.TypeOf<typeof RORAnalyticsTimelineEventOutbound> {}
export interface AnalyticsTimelineEventOutReply extends t.TypeOf<typeof RORAnalyticsTimelineEventOutReply> {}
export interface AnalyticsTimelineEventReminder extends t.TypeOf<typeof RORAnalyticsTimelineEventReminder> {}
export interface AnalyticsTimelineEventReopened extends t.TypeOf<typeof RORAnalyticsTimelineEventReopened> {}
export interface AnalyticsTimelineEventSnoozed extends t.TypeOf<typeof RORAnalyticsTimelineEventSnoozed> {}
export interface AnalyticsTimelineEventTagged extends t.TypeOf<typeof RORAnalyticsTimelineEventTagged> {}
export interface AnalyticsTimelineEventUntagged extends t.TypeOf<typeof RORAnalyticsTimelineEventUntagged> {}

export type AnalyticsTimelineEvent =
  | AnalyticsTimelineEventArchived
  | AnalyticsTimelineEventAssigned
  | AnalyticsTimelineEventUnassigned
  | AnalyticsTimelineEventConvJoined
  | AnalyticsTimelineEventConvSplitted
  | AnalyticsTimelineEventInbound
  | AnalyticsTimelineEventOutbound
  | AnalyticsTimelineEventOutReply
  | AnalyticsTimelineEventMoved
  | AnalyticsTimelineEventReminder
  | AnalyticsTimelineEventReopened
  | AnalyticsTimelineEventSnoozed
  | AnalyticsTimelineEventTagged
  | AnalyticsTimelineEventUntagged;

export interface AnalyticsTimelineEventSource extends t.TypeOf<typeof RAnalyticsTimelineEventSource> {}

export const RAnalyticsTimelineEvent = t.clean<AnalyticsTimelineEvent>(RAnalyticsTimelineEventDirty as any);
export const RAnalyticsTimelineEvents = t.readonlyArray(RAnalyticsTimelineEvent);

/*
 * IO methods.
 */

export function parseAnalyticsTimelineEvent(src: any): AnalyticsTimelineEvent | null {
  const commonProps = {
    type: src.type,
    id: src.id,
    date: src.date,
  };

  switch (src.type) {
    case AnalyticsTimelineEventsEnum.ARCHIVE:
      if (src.reminder_id) {
        return {
          ...commonProps,
          type: AnalyticsTimelineEventsEnum.SNOOZE,
          snoozedBy: getEventSourceLabel(src),
          reminderId: src.reminder_id,
        };
      }
      return {
        ...commonProps,
        archivedBy: getEventSourceLabel(src),
        archivedIn: src.inboxes_full[0].name,
      };
    case AnalyticsTimelineEventsEnum.ASSIGN:
      return {
        ...commonProps,
        assignedBy: getEventSourceLabel(src),
        assignedTo: src.assignee?.display_name,
      };
    case AnalyticsTimelineEventsEnum.UNASSIGN:
      return {
        ...commonProps,
        unassignedBy: getEventSourceLabel(src),
        unassignedFrom: src.assignee?.display_name,
      };
    case AnalyticsTimelineEventsEnum.INBOUND:
    case AnalyticsTimelineEventsEnum.OUTBOUND:
    case AnalyticsTimelineEventsEnum.OUT_REPLY:
      return {
        ...commonProps,
        messageUid: src.message.uid,
      };
    case AnalyticsTimelineEventsEnum.CONV_JOIN:
      return {
        ...commonProps,
        convJoinedBy: getEventSourceLabel(src),
      };
    case AnalyticsTimelineEventsEnum.CONV_SPLIT:
      return {
        ...commonProps,
        convSplittedBy: getEventSourceLabel(src),
      };
    case AnalyticsTimelineEventsEnum.MOVE:
      return {
        ...commonProps,
        movedIn: src.inboxes_full[0].name,
        movedBy: getEventSourceLabel(src),
      };
    case AnalyticsTimelineEventsEnum.REMINDER:
      return {
        ...commonProps,
        reminderBy: getEventSourceLabel(src),
      };
    case AnalyticsTimelineEventsEnum.REOPEN:
      return {
        ...commonProps,
        reopenedIn: src.inboxes_full[0].name,
        reopenedBy: getEventSourceLabel(src),
      };
    case AnalyticsTimelineEventsEnum.TAG:
      return {
        ...commonProps,
        taggedBy: getEventSourceLabel(src),
        tag: parseTag(src.tag),
      };
    case AnalyticsTimelineEventsEnum.UNTAG:
      return {
        ...commonProps,
        untaggedBy: getEventSourceLabel(src),
        tag: parseTag(src.tag),
      };
    default:
      // Ignore unknown events.
      return null;
  }
}

/*
 * Helpers.
 */

export function filterMessageReceivers(recipients: ReadonlyArray<RecipientModel>) {
  return recipients.filter(
    (recipient) => recipient.role === RecipientRolesEnum.TO || recipient.role === RecipientRolesEnum.CC,
  );
}

// Events can be triggered by different things, we do a simple resolution here.
function getEventSourceLabel(src: any): AnalyticsTimelineEventSource {
  if (src.source === ActivitySourceTypesEnum.PUBLIC_API) {
    return {label: 'Public API'};
  } // Public API case.

  if (src.source === ActivitySourceTypesEnum.INACTIVE) {
    return {label: 'Inactive teammate'};
  } // "Conversation has been unassigned because teammate is inactive" case.

  if (src.source === ActivitySourceTypesEnum.RULE) {
    // Rule case.
    return {
      label: src.rule_name,
      ruleId: src.rule_id,
      ruleBundleId: src.rule_bundle_id,
      ruleBundleType: RRuleBundleType.is(src.rule_bundle_type) ? src.rule_bundle_type : undefined,
      ruleNamespace: src.rule_namespace,
    };
  }

  return {label: src.from?.display_name}; // Otherwise we assume it's coming from a teammate.
}
